import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTenantContent } from "../../Context/TenantContentContext";
import { getPublicConsultationReviews } from "../../Services/ConsultationReviewService";
import HomeReviewEntry from "../../Components/Reviews/HomeReviewEntry";
import { ReviewRating } from "../../Components/Reviews/ReviewDetails";
import { formatReviewDate } from "../../Components/Reviews/reviewHelpers";

const TestimonialCard = ({ name, text, rating, createdAt }) => {
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef(null);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;

    if (expanded) {
      setIsOverflowing(true);
      return;
    }

    setIsOverflowing(el.scrollHeight > el.clientHeight + 2);
  }, [text, expanded]);

  return (
    <article className="card border-0 shadow-sm rounded-4 h-100 service-card" dir="rtl">
      <div className="card-body d-flex flex-column">
        <div className="d-flex align-items-center gap-2 mb-3 pt-2">
          <div
            className="rounded-circle bg-green text-white d-flex align-items-center justify-content-center"
            style={{
              width: "40px",
              height: "40px",
              fontSize: "16px",
              fontWeight: "bold",
            }}
          >
            {name?.charAt(0)?.toUpperCase()}
          </div>
          <div className="fw-semibold color-blue">{name}</div>
        </div>

        {rating != null && <ReviewRating rating={rating} />}

        <p
          ref={textRef}
          className="testimonial-text text-secondary-emphasis fs-6 fw-medium text-end mb-0"
          dir="rtl"
          style={
            expanded
              ? {
                  lineHeight: 1.9,
                  textAlign: "justify",
                }
              : {
                  display: "-webkit-box",
                  WebkitLineClamp: 5,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  lineHeight: 1.9,
                  textAlign: "justify",
                }
          }
        >
          {text}
        </p>

        {createdAt && <time className="review-muted review-meta mt-3" dateTime={createdAt}> {formatReviewDate(createdAt)}</time>}
        {isOverflowing && (
          <button
            type="button"
            className="mt-2 align-self-start border-0 bg-transparent p-0 fw-bold iran-sans"
            style={{ color: "#3a8b4f", fontSize: "0.85rem" }}
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
          >
            {expanded ? "نمایش کمتر" : "ادامه مطلب"}
          </button>
        )}
      </div>
    </article>
  );
};

const CommentHome = () => {
  const { content } = useTenantContent();
  const [showAll, setShowAll] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const requesting = useRef(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    getPublicConsultationReviews().then((data) => {
      if (!active) return;
      setReviews(data.items);
      setHasMore(data.hasMore);
      setPage(1);
    }).catch(() => { if (active) setError(true); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [attempt]);

  const loadMore = async () => {
    if (requesting.current) return;
    requesting.current = true;
    setLoading(true);
    setError(false);
    try {
      const data = await getPublicConsultationReviews(page + 1);
      setReviews((items) => [...items, ...data.items.filter((item) => !items.some((existing) => existing.id === item.id))]);
      setHasMore(data.hasMore);
      setPage((value) => value + 1);
    } catch { setError(true); }
    finally { requesting.current = false; setLoading(false); }
  };

  const testimonials = [
    ...reviews.map((review) => ({ ...review, text: review.content, key: `review-${review.id}` })),
    ...(content?.testimonials || []).map((item, index) => ({ ...item, key: `testimonial-${index}` })),
  ];
  const visibleTestimonials = showAll ? testimonials : testimonials.slice(0, 3);

  return (
    <div className="container pt-4 iran-sans" dir="rtl">
      <HomeReviewEntry />
      {loading && <p className="review-muted" role="status">در حال دریافت نظرات بیماران…</p>}
      {error && <p className="review-feedback" role="alert">دریافت نظرات جدید ممکن نشد. <button type="button" className="review-button review-button--quiet" onClick={() => showAll && page > 1 ? loadMore() : setAttempt((value) => value + 1)}>تلاش دوباره</button></p>}
      {!loading && !error && testimonials.length === 0 && <p className="review-muted">هنوز نظری منتشر نشده است. اولین نفری باشید که نظرش را با ما در میان می‌گذارد.</p>}
      <div className="row justify-content-center">
        {visibleTestimonials.map((testimonial) => (
          <div className="col-md-6 col-lg-4 mb-4" key={testimonial.key}>
            <TestimonialCard
              name={testimonial.name}
              text={testimonial.text}
              rating={testimonial.rating}
              createdAt={testimonial.createdAt}
            />
          </div>
        ))}
      </div>

      <div className="row justify-content-center d-flex">
        <div className="col justify-content-center d-flex gap-2">
          {!showAll && (testimonials.length > 3 || hasMore) && (
            <button
              className="btn-clear mt-0 fw-bold rounded-5 iran-sans"
              onClick={() => setShowAll(true)}
            >
              مشاهده بیشتر
            </button>
          )}
          {showAll && (
            <button
              className="btn-clear mt-0 fw-bold rounded-5 iran-sans"
              onClick={() => setShowAll(false)}
            >
              نمایش کمتر
            </button>
          )}
          {showAll && hasMore && <button type="button" className="review-button review-button--quiet" disabled={loading} onClick={loadMore}>{loading ? "در حال دریافت…" : "نظرات بیشتر"}</button>}
        </div>
      </div>
    </div>
  );
};

export default CommentHome;
